import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { DateRange } from 'react-day-picker';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useLanguage } from '@/contexts/LanguageContext';

type Season = {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  min_stay: number;
  price_hera: number;
  price_zeus: number;
  is_active: boolean;
};

const empty: Omit<Season, 'id'> = { name: '', start_date: '', end_date: '', min_stay: 1, price_hera: 0, price_zeus: 0, is_active: true };

export default function AdminSeasons() {
  const { t } = useLanguage();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState('active');

  const load = async () => {
    const { data } = await supabase.from('seasons').select('*').order('start_date');
    setSeasons((data as Season[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const activeSeason = seasons.find(s => s.is_active && s.name.toLowerCase().includes('active')) 
    || seasons.find(s => s.is_active);
  
  const pricingPeriods = seasons.filter(s => s.id !== activeSeason?.id || !activeSeason);

  const save = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error(t('fillRequired'));
      return;
    }
    const payload = { ...form };
    
    if (editingId) {
      const { error } = await supabase.from('seasons').update(payload).eq('id', editingId);
      if (error) { toast.error(t('failedToUpdate') + ': ' + error.message); return; }
      toast.success(t('updatedSuccessfully'));
    } else {
      const { error } = await supabase.from('seasons').insert(payload);
      if (error) { toast.error(t('failedToCreate') + ': ' + error.message); return; }
      toast.success(t('createdSuccessfully'));
    }
    setForm(empty);
    setEditingId(null);
    setShowForm(false);
    load();
  };

  const startEdit = (s: Season) => {
    setEditingId(s.id);
    setForm({ name: s.name, start_date: s.start_date, end_date: s.end_date, min_stay: s.min_stay, price_hera: s.price_hera, price_zeus: s.price_zeus, is_active: s.is_active });
    setShowForm(true);
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from('seasons').delete().eq('id', id);
    if (error) { toast.error(t('failedToDelete') + ': ' + error.message); return; }
    toast.success(t('deleted'));
    load();
  };

  const isActiveSeason = tab === 'active';

  const renderForm = () => {
    if (!showForm) return null;
    if (tab === 'active' && activeSeason && editingId !== activeSeason.id) return null;
    if (tab === 'pricing' && editingId && activeSeason && editingId === activeSeason.id) return null;

    return (
      <div className="bg-card border border-border rounded-sm p-6 mb-8">
        <h3 className="font-serif text-xl mb-4">{editingId ? t('update') : t('create')} {isActiveSeason ? t('activeSeason') : t('pricingPeriods')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-muted-foreground tracking-wider uppercase mb-1 block">{t('name')}</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-accent"
              placeholder={isActiveSeason ? 'e.g. Active Season 2025' : 'e.g. Peak Summer'} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground tracking-wider uppercase mb-1 block">{t('dateRange')}</label>
            <Popover modal={true}>
              <PopoverTrigger asChild>
                <button className={cn("w-full flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm text-left", !form.start_date && "text-muted-foreground")}>
                  <CalendarIcon size={14} />
                  {form.start_date && form.end_date
                    ? `${format(parseISO(form.start_date), 'dd MMM yyyy')} → ${format(parseISO(form.end_date), 'dd MMM yyyy')}`
                    : t('selectDatesAdmin')}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 pointer-events-auto z-[60]" align="start" sideOffset={4} onOpenAutoFocus={e => e.preventDefault()}>
                <Calendar
                  mode="range"
                  selected={
                    form.start_date
                      ? { from: parseISO(form.start_date), to: form.end_date ? parseISO(form.end_date) : undefined }
                      : undefined
                  }
                  onSelect={(range: DateRange | undefined) => {
                    setForm(p => ({
                      ...p,
                      start_date: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
                      end_date: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
                    }));
                  }}
                  numberOfMonths={2}
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
          {!isActiveSeason && (
            <>
              <div>
                <label className="text-xs text-muted-foreground tracking-wider uppercase mb-1 block">{t('minStay')}</label>
                <input type="number" min={1} value={form.min_stay} onChange={e => setForm(p => ({ ...p, min_stay: Number(e.target.value) }))}
                  className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-accent" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground tracking-wider uppercase mb-1 block">{t('heraPriceNight')}</label>
                <input type="number" min={0} value={form.price_hera} onChange={e => setForm(p => ({ ...p, price_hera: Number(e.target.value) }))}
                  className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-accent" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground tracking-wider uppercase mb-1 block">{t('zeusPriceNight')}</label>
                <input type="number" min={0} value={form.price_zeus} onChange={e => setForm(p => ({ ...p, price_zeus: Number(e.target.value) }))}
                  className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-accent" />
              </div>
            </>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} className="bg-accent text-accent-foreground px-6 py-2 text-sm tracking-wider uppercase rounded-sm hover:bg-accent/90">{editingId ? t('update') : t('create')}</button>
          <button onClick={() => { setShowForm(false); setEditingId(null); setForm(empty); }} className="px-6 py-2 text-sm text-muted-foreground border border-border rounded-sm hover:bg-muted">{t('cancel')}</button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <h1 className="font-serif text-3xl font-light mb-8">{t('seasonsPricing')}</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="active">{t('activeSeason')}</TabsTrigger>
          <TabsTrigger value="pricing">{t('pricingPeriods')}</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <p className="text-sm text-muted-foreground mb-6">{t('activeSeasonDesc')}</p>

          {activeSeason ? (
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-serif text-xl">{activeSeason.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {format(parseISO(activeSeason.start_date), 'dd MMM yyyy')} → {format(parseISO(activeSeason.end_date), 'dd MMM yyyy')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setTab('active'); startEdit(activeSeason); }} className="p-2 rounded-sm bg-muted text-muted-foreground hover:bg-muted/80"><Pencil size={16} /></button>
                  <button onClick={() => remove(activeSeason.id)} className="p-2 rounded-sm bg-red-50 text-red-600 hover:bg-red-100"><Trash2 size={16} /></button>
                </div>
              </div>
              {showForm && editingId === activeSeason.id && renderForm()}
            </div>
          ) : (
            <div>
              {!showForm && (
                <button onClick={() => { setForm({ ...empty, name: 'Active Season 2025' }); setEditingId(null); setShowForm(true); }}
                  className="flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 text-sm tracking-wider uppercase rounded-sm hover:bg-accent/90">
                  <Plus size={16} /> {t('setActiveSeason')}
                </button>
              )}
              {showForm && tab === 'active' && renderForm()}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pricing">
          <p className="text-sm text-muted-foreground mb-6">{t('pricingPeriodsDesc')}</p>

          <div className="flex items-center justify-between mb-6">
            <div />
            <button onClick={() => { setForm(empty); setEditingId(null); setShowForm(true); }}
              className="flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 text-sm tracking-wider uppercase rounded-sm hover:bg-accent/90">
              <Plus size={16} /> {t('addPeriod')}
            </button>
          </div>

          {showForm && tab === 'pricing' && renderForm()}

          <div className="grid gap-4">
            {pricingPeriods.map(s => (
              <div key={s.id} className="bg-card border border-border rounded-sm p-6 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-serif text-xl">{s.name}</h3>
                    {!s.is_active && <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-sm">{t('inactive')}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {format(parseISO(s.start_date), 'dd MMM yyyy')} → {format(parseISO(s.end_date), 'dd MMM yyyy')} · Min {s.min_stay} {t('nights').toLowerCase()}
                  </p>
                  <div className="flex gap-6 mt-2">
                    <p className="text-sm"><span className="text-muted-foreground">Hera:</span> <span className="font-medium">€{s.price_hera}/{t('nights').toLowerCase().slice(0, -1)}</span></p>
                    <p className="text-sm"><span className="text-muted-foreground">Zeus:</span> <span className="font-medium">€{s.price_zeus}/{t('nights').toLowerCase().slice(0, -1)}</span></p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setTab('pricing'); startEdit(s); }} className="p-2 rounded-sm bg-muted text-muted-foreground hover:bg-muted/80"><Pencil size={16} /></button>
                  <button onClick={() => remove(s.id)} className="p-2 rounded-sm bg-red-50 text-red-600 hover:bg-red-100"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
            {pricingPeriods.length === 0 && <p className="text-center text-muted-foreground py-12">{t('noPricingPeriods')}</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
